import React from "react";
import { QuestionDTO, CourseEvaluationAnswerDTO } from "../../../services/apiClient";
import { QuestionSetConstants } from "../../../constants/QuestionSetConstants";
import Card from "../../common/Card";
import Button from "../../common/Button";
import ImagePreview from "../../common/ImagePreview";
import { CircleX, RotateCcw, Frown, PartyPopper, TriangleAlert, LockKeyhole, Info } from "lucide-react";

type SectionResult = {
    total: number;
    correct: number;
    answered: number;
    questions: QuestionDTO[];
};

type EvaluationResultsData = {
    totalQuestions: number;
    correctAnswers: number;
    percentage: number;
    sectionResults: {
        sectionA: SectionResult;
        sectionB1: SectionResult;
        sectionB2: SectionResult;
        sectionC: SectionResult;
        sectionD: SectionResult;
    };
};

interface Props {
    evaluationAnswers: CourseEvaluationAnswerDTO[];
    relevantSections: string[];
    userAnswersMap: Record<string, string>;
    results: EvaluationResultsData;
    onRetry?: () => void;
}

const EvaluationResults: React.FC<Props> = ({ evaluationAnswers, relevantSections, userAnswersMap, results, onRetry }) => {

    const renderQuestionResult = (question: QuestionDTO, index: number, showCorrectAnswers: boolean) => {
        const userAnswerId = userAnswersMap[question.id || ''];
        const correctOption = question.questionOptions?.find(option => option.isCorrectOption);
        const userOption = question.questionOptions?.find(option => option.id === userAnswerId);
        const isAnswered = !!userAnswerId;

        return (
            <div key={question.id || index} className="border rounded-lg p-4 mb-4">
                <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold flex-1">
                        {index + 1}. {question.title}
                    </h4>
                    <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${isAnswered
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                        }`}>
                        {isAnswered ? (
                            <>
                                <CircleX className="w-4 h-4" />
                                Incorrect
                            </>
                        ) : (
                            <>
                                <RotateCcw className="w-4 h-4" />
                                Unanswered
                            </>
                        )}
                    </div>
                </div>

                {/* Question Image */}
                {question.imageId && (
                    <div className="mb-3">
                        <ImagePreview fileId={question.imageId} size="md" />
                    </div>
                )}

                <div className="space-y-2">
                    <div className="flex items-center">
                        <span className="font-medium text-gray-600 mr-2">Your Answer:</span>
                        <span className={`px-2 py-1 rounded ${isAnswered
                            ? 'bg-red-50 text-red-700'
                            : 'bg-gray-50 text-gray-700'
                            }`}>
                            {userOption?.title || 'No answer selected'}
                        </span>
                    </div>

                    {showCorrectAnswers && (
                        <div className="flex items-center">
                            <span className="font-medium text-gray-600 mr-2">Correct Answer:</span>
                            <span className="px-2 py-1 rounded bg-green-50 text-green-700">
                                {correctOption?.title || 'N/A'}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderSectionResults = (sectionName: string, sectionData: SectionResult) => {
        if (sectionData.total === 0) return null;

        const percentage = Math.round((sectionData.correct / sectionData.total) * 100);
        const hasIncorrectAnswers = sectionData.questions.length > 0;
        const allQuestionsAnsweredCorrectly = sectionData.correct === sectionData.total; // All questions in section answered correctly
        const hasUnansweredQuestions = sectionData.total > sectionData.answered;

        return (
            <Card key={sectionName} title={sectionName}>
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center text-lg">
                        <span>Section Score:</span>
                        <span className={`font-bold ${percentage >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                            {sectionData.correct}/{sectionData.total} ({percentage}%)
                        </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                        Answered: {sectionData.answered} • Unanswered: {sectionData.total - sectionData.answered}
                    </div>
                </div>

                {hasIncorrectAnswers ? (
                    <div className="space-y-4">
                        <h4 className="font-medium text-gray-700 mb-3">Questions Answered Incorrectly:</h4>
                        {sectionData.questions.map((question: QuestionDTO, index: number) =>
                            renderQuestionResult(question, index, results.percentage >= 70)
                        )}
                        {hasUnansweredQuestions && (
                            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-center gap-2 text-sm text-yellow-800">
                                    <TriangleAlert className="w-4 h-4" />
                                    <span>Note: {sectionData.total - sectionData.answered} unanswered questions in this section also count as incorrect.</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : allQuestionsAnsweredCorrectly ? (
                    <div className="text-center py-6 text-green-600">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <PartyPopper className="w-5 h-5" />
                            <span>Perfect! You got all answered questions correct in this section.</span>
                        </div>
                        {hasUnansweredQuestions && (
                            <div className="mt-2 text-sm text-yellow-600">
                                ({sectionData.total - sectionData.answered} questions were not answered)
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-6 text-gray-500">
                        {sectionData.answered === 0 ? (
                            // No questions answered at all
                            <div>
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Info className="w-5 h-5" />
                                    <span>No questions were answered in this section.</span>
                                </div>
                                <div className="text-sm">
                                    {sectionData.total} unanswered questions count as incorrect.
                                </div>
                            </div>
                        ) : (
                            // Some questions answered correctly, some unanswered, none answered incorrectly
                            <div>
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Info className="w-5 h-5" />
                                    <span>You answered {sectionData.correct} question{sectionData.correct !== 1 ? 's' : ''} correctly in this section.</span>
                                </div>
                                {hasUnansweredQuestions && (
                                    <div className="text-sm text-yellow-600">
                                        {sectionData.total - sectionData.answered} unanswered questions count as incorrect.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Card>
        );
    };

    return (
        <div className="space-y-6">
            {/* Overall Results Header */}
            <Card title="Evaluation Results">
                <div className="text-center mb-6">
                    <div className={`text-6xl font-bold mb-2 ${results.percentage >= 70 ? 'text-green-600' : 'text-red-600'
                        }`}>
                        {results.percentage}%
                    </div>
                    <div className="text-xl text-gray-600 mb-4">
                        {results.correctAnswers} out of {results.totalQuestions} questions correct
                    </div>
                    <div className="text-sm text-gray-500 mb-4">
                        Answered: {evaluationAnswers.length} • Unanswered: {results.totalQuestions - evaluationAnswers.length}
                    </div>

                    {/* Warning for many unanswered questions */}
                    {(results.totalQuestions - evaluationAnswers.length) > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                            <div className="flex items-center gap-2">
                                <TriangleAlert className="w-5 h-5 text-yellow-600" />
                                <span className="text-yellow-800 text-sm">
                                    You have {results.totalQuestions - evaluationAnswers.length} unanswered questions.
                                    These count as incorrect answers.
                                </span>
                            </div>
                        </div>
                    )}

                    <div className={`text-lg font-semibold flex items-center justify-center gap-2 ${results.percentage >= 70 ? 'text-green-600' : 'text-red-600'
                        }`}>
                        {results.percentage >= 70 ? (
                            <>
                                <PartyPopper className="w-5 h-5" />
                                Congratulations! You passed!
                            </>
                        ) : (
                            <>
                                <Frown className="w-5 h-5" />
                                Unfortunately, you did not pass. You need 70% or higher.
                            </>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                {results.percentage < 70 && onRetry && (
                    <div className="flex justify-center space-x-4">
                        <Button
                            onClick={onRetry}
                            variant="secondary"
                            icon={RotateCcw}>
                            Retry Evaluation
                        </Button>
                    </div>
                )}

            </Card>

            {/* Section Breakdown */}
            {results.percentage < 100 ? (
                <div className="grid gap-6">
                    <Card title="Questions to Review">
                        <p className="text-gray-600 mb-4">
                            Below are sections where you didn't achieve a perfect score. Review these topics for future reference.
                        </p>
                        {results.percentage < 70 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <LockKeyhole className="w-5 h-5 text-amber-600" />
                                    <span className="text-amber-800 text-sm">
                                        Correct answers are only revealed to candidates who achieve a passing score of 70% or higher.
                                    </span>
                                </div>
                            </div>
                        )}
                    </Card>
                    {relevantSections.includes(QuestionSetConstants.SECTION_A) && renderSectionResults('Section A', results.sectionResults.sectionA)}
                    {relevantSections.includes(QuestionSetConstants.SECTION_B1) && renderSectionResults('Section B1', results.sectionResults.sectionB1)}
                    {relevantSections.includes(QuestionSetConstants.SECTION_B2) && renderSectionResults('Section B2', results.sectionResults.sectionB2)}
                    {relevantSections.includes(QuestionSetConstants.SECTION_C) && renderSectionResults('Section C', results.sectionResults.sectionC)}
                    {relevantSections.includes(QuestionSetConstants.SECTION_D) && renderSectionResults('Section D', results.sectionResults.sectionD)}
                </div>
            ) : (
                <Card title="Excellent Work!">
                    <div className="text-center py-6">
                        <div className="text-4xl mb-4">🏆</div>
                        <h3 className="text-xl font-semibold text-green-600 mb-2">Perfect Score!</h3>
                        <p className="text-gray-600">
                            You answered all {results.totalQuestions} questions correctly. Great job!
                        </p>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default EvaluationResults;
